import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { Producto, Categoria, Deposito, PrecioRegla } from '@/types/database'

const QK = {
  productos: ['productos'] as const,
  categorias: ['categorias'] as const,
  depositos: ['depositos'] as const,
}

export function useProductos() {
  return useQuery({
    queryKey: QK.productos,
    queryFn: () => api.get<Producto[]>('/api/productos'),
    refetchInterval: 30_000,
  })
}

export function useCategorias() {
  return useQuery({
    queryKey: QK.categorias,
    queryFn: () => api.get<Categoria[]>('/api/categorias'),
    staleTime: 5 * 60_000,
  })
}

export function useDepositos() {
  return useQuery({
    queryKey: QK.depositos,
    queryFn: () => api.get<Deposito[]>('/api/depositos'),
    staleTime: 10 * 60_000,
  })
}

export interface ProductoFormData {
  nombre: string
  marca: string
  codigo_barras: string
  sku: string
  categoria_id: string
  stock_minimo: number
  precio_costo: number
  precio_minorista: number
  precio_mayorista: number
}

export const FORM_VACIO: ProductoFormData = {
  nombre: '',
  marca: '',
  codigo_barras: '',
  sku: '',
  categoria_id: '',
  stock_minimo: 0,
  precio_costo: 0,
  precio_minorista: 0,
  precio_mayorista: 0,
}

export function useCrearProducto() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ form, depositoId, fotoUrl }: { form: ProductoFormData; depositoId: string; fotoUrl?: string | null }) => {
      // 1. Crear producto (con deposito_id para stock inicial)
      const prod = await api.post<{ id: string }>('/api/productos', {
        nombre:       form.nombre.trim(),
        marca:        form.marca.trim() || null,
        codigo_barras: form.codigo_barras.trim() || null,
        sku:          form.sku.trim() || null,
        categoria_id: form.categoria_id || null,
        stock_minimo: form.stock_minimo,
        foto_url:     fotoUrl ?? null,
        deposito_id:  depositoId,
      })

      // 2. Presentacion "Unidad"
      const pres = await api.post<{ id: string }>(`/api/productos/${prod.id}/presentaciones`, {
        nombre: 'Unidad', factor_conversion: 1,
      })

      // 3. Precios (solo los mayores a 0)
      const precios = [
        { tipo: 'costo',      precio: form.precio_costo },
        { tipo: 'minorista',  precio: form.precio_minorista },
        { tipo: 'mayorista',  precio: form.precio_mayorista },
      ].filter(p => p.precio > 0)

      for (const p of precios) {
        await api.post('/api/productos/precios/nuevo', {
          producto_id: prod.id, presentacion_id: pres.id, tipo: p.tipo, precio: p.precio,
        })
      }

      return prod
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.productos }),
  })
}

export function useActualizarProducto() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, form, presentacionId, fotoUrl }: { id: string; form: ProductoFormData; presentacionId: string; fotoUrl?: string | null }) => {
      await api.put(`/api/productos/${id}`, {
        nombre:       form.nombre.trim(),
        marca:        form.marca.trim() || null,
        codigo_barras: form.codigo_barras.trim() || null,
        sku:          form.sku.trim() || null,
        categoria_id: form.categoria_id || null,
        stock_minimo: form.stock_minimo,
        ...(fotoUrl !== undefined && { foto_url: fotoUrl }),
      })

      // Reemplazar precios: desactivar anteriores e insertar nuevos
      const precios = [
        { tipo: 'costo',      precio: form.precio_costo },
        { tipo: 'minorista',  precio: form.precio_minorista },
        { tipo: 'mayorista',  precio: form.precio_mayorista },
      ].filter(p => p.precio > 0)

      for (const p of precios) {
        await api.post('/api/productos/precios/nuevo', {
          producto_id: id, presentacion_id: presentacionId, tipo: p.tipo, precio: p.precio,
        })
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.productos }),
  })
}

export function useToggleActivo() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, activo }: { id: string; activo: boolean }) =>
      api.put(`/api/productos/${id}`, { activo }),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.productos }),
  })
}

export function useReglasCantidad(productoId: string | null | undefined) {
  return useQuery({
    queryKey: ['reglas-cantidad', productoId],
    queryFn: () => api.get<PrecioRegla[]>(`/api/productos/reglas/all?producto_id=${productoId}`),
    enabled: !!productoId,
    staleTime: 30_000,
  })
}

export function useTodasLasReglas() {
  return useQuery({
    queryKey: ['reglas-cantidad-all'],
    queryFn: () => api.get<PrecioRegla[]>('/api/productos/reglas/all'),
    staleTime: 60_000,
  })
}

export function useGuardarRegla() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (regla: {
      id?: string
      producto_id: string
      cantidad_minima: number
      tipo_precio: 'mayorista' | 'personalizado'
      precio_especial: number | null
      descripcion: string | null
    }) => {
      if (regla.id) {
        return api.put(`/api/productos/reglas/${regla.id}`, regla)
      }
      return api.post('/api/productos/reglas', regla)
    },
    onSuccess: (_data, regla) => {
      qc.invalidateQueries({ queryKey: ['reglas-cantidad', regla.producto_id] })
      qc.invalidateQueries({ queryKey: ['reglas-cantidad-all'] })
    },
  })
}

export function useEliminarRegla() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id }: { id: string; productoId: string }) =>
      api.put(`/api/productos/reglas/${id}`, { activo: false }),
    onSuccess: (_data, { productoId }) => {
      qc.invalidateQueries({ queryKey: ['reglas-cantidad', productoId] })
      qc.invalidateQueries({ queryKey: ['reglas-cantidad-all'] })
    },
  })
}

export function useAjustarStock() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      productoId, depositoId, tipo, cantidad, notas, numero_lote, fecha_vencimiento,
    }: {
      productoId: string
      depositoId: string
      tipo: 'entrada' | 'salida' | 'ajuste'
      cantidad: number
      notas: string
      usuarioId: string
      numero_lote?: string
      fecha_vencimiento?: string
    }) =>
      api.post('/api/stock/movimiento', {
        producto_id:      productoId,
        deposito_id:      depositoId,
        tipo,
        cantidad,
        notas:            notas.trim() || null,
        numero_lote:      numero_lote?.trim() || null,
        fecha_vencimiento: fecha_vencimiento || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.productos })
      qc.invalidateQueries({ queryKey: ['stock-page'] })
      qc.invalidateQueries({ queryKey: ['dashboard-stats'] })
      qc.invalidateQueries({ queryKey: ['dashboard-alertas'] })
      qc.invalidateQueries({ queryKey: ['dashboard-movimientos'] })
      qc.invalidateQueries({ queryKey: ['movimientos-recientes'] })
    },
  })
}

// ── Ingredientes / Recetas ────────────────────────────────────────────────────

export interface Ingrediente {
  id: string
  producto_id: string
  ingrediente_id: string
  cantidad: number
  ingrediente_nombre: string
  ingrediente_sku: string | null
}

export function useIngredientes(productoId: string | null | undefined) {
  return useQuery({
    queryKey: ['ingredientes', productoId],
    queryFn: () => api.get<Ingrediente[]>(`/api/ingredientes?producto_id=${productoId}`),
    enabled: !!productoId,
    staleTime: 30_000,
  })
}

export function useAgregarIngrediente() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { producto_id: string; ingrediente_id: string; cantidad: number }) =>
      api.post('/api/ingredientes', data),
    onSuccess: (_data, { producto_id }) => {
      qc.invalidateQueries({ queryKey: ['ingredientes', producto_id] })
    },
  })
}

export function useEliminarIngrediente() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id }: { id: string; productoId: string }) =>
      api.del(`/api/ingredientes/${id}`),
    onSuccess: (_data, { productoId }) => {
      qc.invalidateQueries({ queryKey: ['ingredientes', productoId] })
    },
  })
}

export function useEliminarProducto() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.del(`/api/productos/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.productos })
      qc.invalidateQueries({ queryKey: ['stock-page'] })
      qc.invalidateQueries({ queryKey: ['dashboard-stats'] })
      qc.invalidateQueries({ queryKey: ['dashboard-alertas'] })
    },
  })
}

// ── Helpers (sin cambios) ─────────────────────────────────────────────────────

export function getStockTotal(producto: Producto): number {
  return (producto.stock ?? []).reduce((s, r) => s + r.cantidad_actual, 0)
}

export function getStockDeposito(producto: Producto, depositoId: string | null): number {
  if (!depositoId) return getStockTotal(producto)
  return producto.stock?.find(s => s.deposito_id === depositoId)?.cantidad_actual ?? 0
}

export function getStockStatus(producto: Producto): 'ok' | 'bajo' | 'sin' {
  const total = getStockTotal(producto)
  if (total === 0) return 'sin'
  if (total < producto.stock_minimo) return 'bajo'
  return 'ok'
}

export function getPrecio(producto: Producto, tipo: 'costo' | 'minorista' | 'mayorista'): number {
  const pres = producto.producto_presentaciones?.[0]
  if (!pres) return 0
  const precio = pres.precios?.find(p => p.tipo === tipo && p.activo)
  return precio?.precio ?? 0
}
